import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Switch, Route, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import { ModalContext } from 'App';
import { getJson } from 'session';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import EmployeeUserManagementContent from './EmployeeUserManagementContent';
import useDebounce from '../../../common/components/UseDebounce';
import ChangePasswordModal from '../components/ChangePasswordModal';
import CreateEmployeeModal from '../modals/CreateEmployeeModal';
import UpdateEmployeeModal from '../modals/UpdateEmployeeModal';

const form = {
  keyword: '',
};

function EmployeeUserManagementContainer({
  match,
  history,
  getUserManagementList,
  userManagementList,
  userManagementListPager,
  isLoading,
  deleteUserManagement,
  showUserManagement,
  updateAdminStatus,
}) {
  const [formData, setFormData] = useState(form);
  const setModal = useContext(ModalContext);

  const profile = getJson('profile') || {};

  const filteredList = userManagementList.filter(
    (v) => _.get(v, 'included.roles.attributes.name') !== 'super_administrator'
  );

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModal = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <CreateEmployeeModal />,
      title: 'Add User',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <UpdateEmployeeModal updateList={data.attributes} id={data.id} />
      ),
      title: 'Update User',
      modalSize: 'modal-md',
    });
  };

  const handleChangePassword = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <ChangePasswordModal updateList={data.attributes} id={data.id} />
      ),
      title: 'Change User Password',
      modalSize: 'modal-md',
    });
  };

  const handleDelete = (dataInfo) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure you want to delete this User Management?',
      () => {
        deleteUserManagement(dataInfo.id, () => {
          getUserManagementList({}, (data) => {
            const id = _.get(data, 'data.0.id');
            history.push(`/user-management/employees/${id}`);
            showUserManagement(id);
          });
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  const handleToggleUserStatus = (data) => {
    updateAdminStatus(
      data?.id,
      { is_active: !_.get(data, 'attributes.is_active') },
      () => {
        getUserManagementList();
        showUserManagement(data.id);
      }
    );
  };

  // const handleFilter = (e) => {
  //   e.preventDefault();
  //   getUserManagementList({ ...formData });
  // };

  useEffect(() => {
    getUserManagementList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    q: formData.keyword,
    sort_by: 'asc',
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      getUserManagementList({ q: formData.keyword, sort_by: 'asc' });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm]);

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:userManagementId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Employee Management"
            getData={getUserManagementList}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search Transaction..."
            pathName={`${match.path}/:userManagementId?`}
            listName="Transaction"
            label="City Hall Employee List"
            renderItem={(item) => (
              <div className="px-3 py-3">
                <div className="transaction-list-company">
                  <span> {_.get(item, 'attributes.first_name')} </span>{' '}
                  <span>{_.get(item, 'attributes.last_name')}</span>
                </div>
                <h4 className="mt-1 mb-0 transaction-list-name">
                  {_.get(item, 'included.roles.attributes.name')}
                </h4>
              </div>
            )}
            isLoading={isLoading}
            selected={params.userManagementId}
            data={
              _.get(profile, 'roles') === 'super_administrator'
                ? userManagementList
                : filteredList
            }
            dataPager={userManagementListPager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:userManagementId?`}
                    render={() => (
                      <EmployeeUserManagementContent
                        handleAddModal={handleAddModal}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleChangePassword={handleChangePassword}
                        handleToggleUserStatus={handleToggleUserStatus}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:userManagementId?`}
                  render={() =>
                    !params.userManagementId &&
                    userManagementList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(userManagementList, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

EmployeeUserManagementContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getUserManagementList: PropTypes.instanceOf(Function).isRequired,
  userManagementList: PropTypes.instanceOf(Array).isRequired,
  userManagementListPager: PropTypes.instanceOf(Object).isRequired,
  deleteUserManagement: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  showUserManagement: PropTypes.instanceOf(Function).isRequired,
  updateAdminStatus: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  userManagementList: _.get(api, `${c.GET_USER_MANAGEMENT}.list`) || [],
  userManagementListPager: _.get(api, `${c.GET_USER_MANAGEMENT}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_USER_MANAGEMENT) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(EmployeeUserManagementContainer);
